<script setup>
import CustomFieldValue from "@/components/customFields/CustomFieldValue.vue";
import ReviewEdit from "@/components/trackers/ReviewEdit.vue";
import Avatar from "vue-avatar";

import {computed, ref} from "vue";
import {cutString} from "../../helpers/string";
import trackersReviewsService from "@/services/trackers-reviews.service";

const {trackerId, hasOverallScore} = defineProps(['trackerId', 'hasOverallScore']);
const emit = defineEmits(['reviewSaved']);

const showModal = ref(false);
const fullReviewIndex = ref(0);
const reviews = ref([]);
const companyId = ref(null);
const editReview = ref(null);

const title = computed(() => fullReviewIndex.value ? reviews.value[fullReviewIndex.value-1].user_name + "'s review" : "Ratings");
const currentReview = computed(() => reviews.value[fullReviewIndex.value - 1]);

async function getData() {
    reviews.value = [];

    const {data} = await trackersReviewsService.getReviews(trackerId, companyId.value);
    reviews.value = data;
    showModal.value = true;
}

function updateReviews() {
    getData();
    emit('reviewSaved', companyId.value)
}

function open(trackerId, company_id) {
    fullReviewIndex.value = 0;
    companyId.value = company_id;

    getData();
}

defineExpose({
    open,
})
</script>

<template>
    <b-modal v-model="showModal" hide-footer modal-class="modal-w-md modal-rating">
        <template #modal-header>
            <a v-if="fullReviewIndex" class="modal__back-link" @click="fullReviewIndex = 0"></a>
            <h5 class="modal-title mr-auto">{{title}}</h5>
            <button @click="showModal = false" class="close"></button>
        </template>

        <ul class="ratings-list">
            <template v-if="fullReviewIndex">
                <li class="ratings-list__owner ratings-list__owner--white">
                    <header v-if="!hasOverallScore" class="ratings-list__header">
                        <div class="ratings-list__avatar">
                            <Avatar
                                :username="currentReview.user_name"
                                :src="currentReview.user_avatar"
                                :size="30">
                            </Avatar>
                        </div>

                        <div class="ratings-list__author" :class="{deleted: currentReview.user_deleted}">{{ currentReview.user_name }}</div>

                        <div class="rating-section">
                            <div class="rating">
                                <span v-for="index in [1,2,3,4,5]" :key="index" :class="{'rating__current': currentReview.interest_level >= index}"></span>
                            </div>
                        </div>
                    </header>

                    <div v-else style="display: flex; justify-content: space-between; font-weight: bold">
                        <div>
                            Overall rating
                        </div>
                        <div>
                            {{currentReview.overall_score}}/{{currentReview.max_overall_score}}
                        </div>
                    </div>

                    <div :class="{'ratings-list__text': !currentReview.overall_score}">
                        <ul class="ratings-list__sub">
                            <li>{{currentReview.message}}</li>
                            <li v-for="(field, fieldIndex) in currentReview.fields" :key="fieldIndex">
                                <b class="ratings-list__sub__title">{{field.field.name}}</b>

                                <CustomFieldValue
                                    :editable="false"
                                    :field="field.field"
                                    :init-value="field.value"
                                />
                            </li>
                        </ul>

                        <div class="text-right" v-if="currentReview.can_edit">
                            <a class="ratings-list__edit" @click="editReview.open(companyId)">Edit</a>
                        </div>
                    </div>
                </li>
            </template>

            <template v-else>
                <li v-for="(review, index) in reviews" :key="index" class="ratings-list__owner">
                    <header class="ratings-list__header">
                        <div class="ratings-list__avatar">
                            <Avatar
                                :username="review.user_name"
                                :src="review.user_avatar"
                                :size="30"
                            />
                        </div>

                        <div class="ratings-list__author" :class="{deleted: review.user_deleted}">{{ review.user_name }}</div>

                        <div class="rating-section">
                            <div class="rating-section__overal" v-if="hasOverallScore">
                                Rating: <span class="rating-section__overal__num">{{review.overall_score}}/{{review.max_overall_score}}</span>
                            </div>

                            <div v-else class="rating">
                                <span v-for="index in [1,2,3,4,5]" :key="index" :class="{'rating__current': review.interest_level >= index}"></span>
                            </div>
                        </div>
                    </header>
                    <div class="ratings-list__text">
                        <p>
                            {{ cutString(review.message, 100)}}
                            <template v-if="review.message.length > 100">...</template> <a class="link" v-if="review.message.length > 100 || review.fields.length" @click="fullReviewIndex = index + 1">View more</a>
                        </p>

                        <div class="text-right" v-if="review.can_edit">
                            <a class="ratings-list__edit" @click="editReview.open(companyId)">Edit</a>
                        </div>
                    </div>
                </li>
            </template>
        </ul>

        <ReviewEdit
            ref="editReview"
            :tracker-id="trackerId"
            @reviewSaved="updateReviews"
        />
    </b-modal>
</template>

<style scoped>

</style>
